import {
  HHButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import AddIcon from '@mui/icons-material/Add';
import { useCallback } from 'react';
import { PackageForm } from '../components/packages/package-form/form';
import { PackageFormValues } from '../components/packages/package-form/validation-schema';
import { PackageGrid } from '../components/packages/package-grid';
import {
  DynamicDrawerProvider,
  useDynamicDrawerContext,
} from '../context/dynamic-drawer.context';
import { useSnackbarContext } from '../context/snackbar.context';
import { useBillingWriteAccess } from '../hooks/use-billing-write-access';
import {
  GetAllPackagesDocument,
  PackageOfferingEnum,
  useCreatePackageMutation,
  useGetAllPackagesQuery,
} from '../types';

export const PACKAGES_TEST_ID = 'packages-container';

const PackagesContent = (): JSX.Element => {
  const canWrite = useBillingWriteAccess();
  const { setDrawerContent, onClose } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();

  const [createPackage] = useCreatePackageMutation({
    refetchQueries: [{ query: GetAllPackagesDocument }],
  });
  const { data: packagesData } = useGetAllPackagesQuery();

  const handleCreate = useCallback(
    async (values: PackageFormValues): Promise<void> => {
      try {
        await createPackage({
          variables: {
            packageInput: {
              name: values.name.trim(),
              packageOffering: values.offeringType as PackageOfferingEnum,
              programIndications:
                values.offeringType === PackageOfferingEnum.ProgramIndication
                  ? values.programIndications
                  : null,
              productId:
                values.offeringType === PackageOfferingEnum.Product
                  ? (values.productId as number)
                  : null,
            },
          },
        });
        setSnackbarState('Package created successfully', 'success', 3000);
        onClose();
      } catch {
        setSnackbarState('Error creating package', 'error', 3000);
      }
    },
    [createPackage, setSnackbarState, onClose],
  );

  const handleAddPackage = useCallback((): void => {
    const existingPackages = packagesData?.getAllPackages ?? [];
    setDrawerContent(
      <HHStack padding={4}>
        <PackageForm
          onSubmit={handleCreate}
          existingPackages={existingPackages}
        />
      </HHStack>,
      <HHTypography hhVariant="h6">Add Package</HHTypography>,
      '35%',
      'temporary',
    );
  }, [setDrawerContent, handleCreate, packagesData]);

  return (
    <HHStack
      flex={1}
      direction="column"
      spacing={4}
      minHeight="0"
      data-testid={PACKAGES_TEST_ID}
    >
      <HHStack
        sx={{ paddingLeft: 6, paddingRight: 6, paddingTop: 6 }}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <HHTypography hhVariant="section-title">Packages</HHTypography>
        {canWrite && (
          <HHButton
            hhVariant="variant-bypass"
            variant="contained"
            size="medium"
            startIcon={<AddIcon />}
            onClick={handleAddPackage}
            data-testid="add-package-button"
            sx={{ minWidth: 120 }}
          >
            Add Package
          </HHButton>
        )}
      </HHStack>
      <PackageGrid />
    </HHStack>
  );
};

const Packages = (): JSX.Element => (
  <DynamicDrawerProvider>
    <PackagesContent />
  </DynamicDrawerProvider>
);

export default Packages;
